/**
 * WardrobePage — outfits + currently worn + carried inventory.
 *
 * Layout, top to bottom:
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │  Wardrobe                                       [+ Save look]    │
 *   │                                                                   │
 *   │  My Outfits                                                       │
 *   │  ─────────                                                        │
 *   │  [grid of OutfitCard]                                             │
 *   │                                                                   │
 *   │  Currently Worn                              [ Undress ]          │
 *   │  ─────────────                                                    │
 *   │  [PaperDoll]    [list of ItemCard rows]                           │
 *   │                                                                   │
 *   │  Inventory                                                        │
 *   │  ─────────                                                        │
 *   │  [grid of ItemCard]                                               │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * Mutations land via two paths:
 *   - REST hooks (useCreateOutfit, useDeleteOutfit, useUpdateOutfit, etc.)
 *     handle outfit CRUD — invalidation is owned by those hooks.
 *   - Equip/unequip, apply/undress, and the item-detail action row
 *     (drop/give/put-in/deposit/secure, #1909) travel through the websocket
 *     action dispatcher. We listen for the `ACTION_RESULT` reply on the bus
 *     and invalidate inventory + equipped queries on success.
 *
 * The dispatcher's `objectdb_target_kwargs` resolve `<name>_id` kwargs
 * against `ObjectDB.pk` — items carry that as `game_object_id`, distinct
 * from the `id` (ItemInstance pk) used to key React state/props.
 */

import { useCallback, useMemo, useState } from 'react';
import { Hammer, Plus, Shirt } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useAppSelector } from '@/store/hooks';
import { useGameSocket } from '@/hooks/useGameSocket';
import { useActionResult } from '@/hooks/actionResultBus';
import type { ActionResultPayload, RoomStateObject } from '@/hooks/types';
import { useMyRosterEntriesQuery } from '@/roster/queries';
import { ItemCard } from '../components/ItemCard';
import { ItemDetailPanel } from '../components/ItemDetailPanel';
import { GiveItemDialog } from '../components/GiveItemDialog';
import { PutInDialog } from '../components/PutInDialog';
import { OutfitCard } from '../components/OutfitCard';
import { PaperDoll, type EquippedItemDisplay } from '../components/PaperDoll';
import { SaveOutfitDialog } from '../components/SaveOutfitDialog';
import { CreateItemDialog } from '../components/CreateItemDialog';
import { EditOutfitDialog } from '../components/EditOutfitDialog';
import { DeleteOutfitDialog } from '../components/DeleteOutfitDialog';
import { UndressButton } from '../components/UndressButton';
import { useEquippedItems, useInventory, inventoryKeys } from '../hooks/useInventory';
import { useOutfits } from '../hooks/useOutfits';
import type { ContainerAccessPolicy, ItemInstance } from '../types';

export function WardrobePage() {
  const activeCharacter = useAppSelector((state) => state.game.active);
  const { data: myEntries = [] } = useMyRosterEntriesQuery();

  // Resolve the active character name to its underlying ObjectDB pk. The pk
  // doubles as the CharacterSheet pk because CharacterSheet is OneToOne with
  // ObjectDB via primary_key=True.
  const activeEntry = useMemo(
    () => myEntries.find((entry) => entry.name === activeCharacter) ?? null,
    [myEntries, activeCharacter]
  );
  const characterId = activeEntry?.character_id ?? undefined;
  const characterSheetId = characterId; // Same pk by design.

  const { data: outfits = [] } = useOutfits(characterSheetId);
  const { data: inventory = [] } = useInventory(characterId);
  const { data: equipped = [] } = useEquippedItems(characterId);

  const { executeAction } = useGameSocket();
  const queryClient = useQueryClient();

  const [saveOpen, setSaveOpen] = useState(false);
  const [craftOpen, setCraftOpen] = useState(false);
  // Selection is held by id, not by a captured object (2026-07 audit): the
  // detail panel and outfit editor re-derive their live record from the query
  // caches below, so a mutation that refetches inventory/outfits is reflected
  // immediately instead of leaving a stale click-time snapshot on screen (e.g.
  // adding an outfit slot, changing a container's access policy, using a charge).
  const [editingOutfitId, setEditingOutfitId] = useState<number | null>(null);
  const [deletingOutfitId, setDeletingOutfitId] = useState<number | null>(null);
  const [detailItemId, setDetailItemId] = useState<number | null>(null);
  // Item awaiting a recipient/container pick (#1909) — set by the detail
  // panel's Give/Put-in buttons, consumed once the picker dialog confirms.
  const [givingItemId, setGivingItemId] = useState<number | null>(null);
  const [puttingInItemId, setPuttingInItemId] = useState<number | null>(null);

  // Co-located characters for the Give recipient picker — the room state
  // the game rail already tracks (self is excluded server-side). Empty
  // outside an active room/session.
  const roomCharacters = useAppSelector<RoomStateObject[]>(
    (state) =>
      (activeCharacter ? state.game.sessions[activeCharacter]?.room?.characters : undefined) ?? []
  );

  // -------------------------------------------------------------------------
  // WS action_result subscription
  // -------------------------------------------------------------------------

  const handleActionResult = useCallback(
    (payload: ActionResultPayload) => {
      if (payload.success) {
        if (characterId != null) {
          queryClient
            .invalidateQueries({
              queryKey: inventoryKeys.inventory(characterId),
            })
            .catch(() => {});
          queryClient
            .invalidateQueries({
              queryKey: inventoryKeys.equipped(characterId),
            })
            .catch(() => {});
        }
        if (payload.message) {
          toast.success(payload.message);
        }
      } else {
        toast.error(payload.message ?? 'Action failed.');
      }
    },
    [characterId, queryClient]
  );
  useActionResult(handleActionResult);

  // -------------------------------------------------------------------------
  // Derived data
  // -------------------------------------------------------------------------

  const inventoryById = useMemo(() => {
    const map = new Map<number, ItemInstance>();
    for (const item of inventory) {
      map.set(item.id, item);
    }
    return map;
  }, [inventory]);

  // Wardrobes the player can save outfits into. The backend doesn't surface
  // is_wardrobe in the list serializer (Phase A), so for now we offer the
  // full inventory and let the backend reject non-wardrobes via the
  // NotAContainer validation error. Phase B can narrow this once the field
  // is exposed in ItemTemplateList.
  const reachableWardrobes = useMemo(() => inventory, [inventory]);

  // EquippedItem from REST is a thin join row referring to an item_instance
  // by id. Hydrate against the inventory map so PaperDoll / ItemCard get
  // full ItemInstance objects.
  const equippedItems = useMemo<ItemInstance[]>(() => {
    return equipped
      .map((row) => inventoryById.get(row.item_instance))
      .filter((item): item is ItemInstance => item != null);
  }, [equipped, inventoryById]);

  const equippedDisplay = useMemo<EquippedItemDisplay[]>(() => {
    return equipped
      .map((row): EquippedItemDisplay | null => {
        const item = inventoryById.get(row.item_instance);
        if (!item) return null;
        return {
          id: item.id,
          body_region: row.body_region,
          equipment_layer: row.equipment_layer,
          display_name: item.display_name,
          display_image_url: item.display_image_url,
          quality_color_hex: item.quality_tier?.color_hex ?? '',
        };
      })
      .filter((item): item is EquippedItemDisplay => item != null);
  }, [equipped, inventoryById]);

  const equippedItemIds = useMemo(
    () => new Set(equippedItems.map((item) => item.id)),
    [equippedItems]
  );

  // Live-derived selections (2026-07 audit) — see the id-state comment above.
  // Equipped items are hydrated from `inventory`, so `inventoryById` covers
  // both worn and carried; a null lookup (item left inventory) closes the panel.
  const detailItem = detailItemId != null ? (inventoryById.get(detailItemId) ?? null) : null;
  const editingOutfit =
    editingOutfitId != null ? (outfits.find((o) => o.id === editingOutfitId) ?? null) : null;
  const deletingOutfit =
    deletingOutfitId != null ? (outfits.find((o) => o.id === deletingOutfitId) ?? null) : null;

  // -------------------------------------------------------------------------
  // Action handlers (websocket)
  // -------------------------------------------------------------------------

  const handleApplyOutfit = useCallback(
    (outfitId: number) => {
      if (!activeCharacter) return;
      executeAction(activeCharacter, 'apply_outfit', { outfit_id: outfitId });
    },
    [activeCharacter, executeAction]
  );

  const handleUndress = useCallback(() => {
    if (!activeCharacter) return;
    executeAction(activeCharacter, 'undress', {});
  }, [activeCharacter, executeAction]);

  // Resolve an ItemInstance id (React-facing) to its underlying ObjectDB pk
  // (`game_object_id`) — the id the action dispatcher's `<name>_id` kwargs
  // actually resolve against. Looks up the same inventoryById map equipped
  // items are hydrated from, so it covers both worn and carried items.
  const resolveGameObjectId = useCallback(
    (itemId: number) => inventoryById.get(itemId)?.game_object_id ?? null,
    [inventoryById]
  );

  const handleWear = useCallback(
    (itemId: number) => {
      const targetId = resolveGameObjectId(itemId);
      if (!activeCharacter || targetId == null) return;
      executeAction(activeCharacter, 'equip', { target_id: targetId });
    },
    [activeCharacter, executeAction, resolveGameObjectId]
  );

  const handleRemove = useCallback(
    (itemId: number) => {
      const targetId = resolveGameObjectId(itemId);
      if (!activeCharacter || targetId == null) return;
      executeAction(activeCharacter, 'unequip', { target_id: targetId });
    },
    [activeCharacter, executeAction, resolveGameObjectId]
  );

  const handleDrop = useCallback(
    (itemId: number) => {
      const targetId = resolveGameObjectId(itemId);
      if (!activeCharacter || targetId == null) return;
      executeAction(activeCharacter, 'drop', { target_id: targetId });
    },
    [activeCharacter, executeAction, resolveGameObjectId]
  );

  const handleGiveClick = useCallback((itemId: number) => {
    setGivingItemId(itemId);
  }, []);

  const handleGiveConfirm = useCallback(
    (recipientId: number) => {
      const targetId = givingItemId != null ? resolveGameObjectId(givingItemId) : null;
      setGivingItemId(null);
      if (!activeCharacter || targetId == null) return;
      executeAction(activeCharacter, 'give', { target_id: targetId, recipient_id: recipientId });
    },
    [activeCharacter, executeAction, givingItemId, resolveGameObjectId]
  );

  const handlePutInClick = useCallback((itemId: number) => {
    setPuttingInItemId(itemId);
  }, []);

  const handlePutInConfirm = useCallback(
    (containerId: number) => {
      const targetId = puttingInItemId != null ? resolveGameObjectId(puttingInItemId) : null;
      const containerTargetId = resolveGameObjectId(containerId);
      setPuttingInItemId(null);
      if (!activeCharacter || targetId == null || containerTargetId == null) return;
      executeAction(activeCharacter, 'put_in', {
        target_id: targetId,
        container_id: containerTargetId,
      });
    },
    [activeCharacter, executeAction, puttingInItemId, resolveGameObjectId]
  );

  const handleDeposit = useCallback(
    (itemId: number) => {
      const targetId = resolveGameObjectId(itemId);
      if (!activeCharacter || targetId == null) return;
      executeAction(activeCharacter, 'deposit_coins', { target_id: targetId });
    },
    [activeCharacter, executeAction, resolveGameObjectId]
  );

  const handleSetContainerPolicy = useCallback(
    (itemId: number, policy: ContainerAccessPolicy) => {
      const targetId = resolveGameObjectId(itemId);
      if (!activeCharacter || targetId == null) return;
      executeAction(activeCharacter, 'set_container_policy', { target_id: targetId, policy });
    },
    [activeCharacter, executeAction, resolveGameObjectId]
  );

  const containerCandidates = useMemo(
    () => inventory.filter((item) => item.template?.is_container && item.id !== puttingInItemId),
    [inventory, puttingInItemId]
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (!activeCharacter || !characterId || !characterSheetId) {
    return (
      <div className="container mx-auto px-4 py-12">
        <h1 className="mb-4 text-3xl font-bold">Wardrobe</h1>
        <NoActiveCharacterState />
      </div>
    );
  }

  return (
    <div className="container mx-auto space-y-8 px-4 py-8">
      <header className="flex items-center justify-between gap-4">
        <h1 className="text-3xl font-bold tracking-tight">Wardrobe</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setCraftOpen(true)}>
            <Hammer className="mr-2 h-4 w-4" />
            Craft item
          </Button>
          <Button onClick={() => setSaveOpen(true)} disabled={reachableWardrobes.length === 0}>
            <Plus className="mr-2 h-4 w-4" />
            Save look
          </Button>
        </div>
      </header>

      <section aria-labelledby="outfits-heading" className="space-y-4">
        <SectionHeader id="outfits-heading">My Outfits</SectionHeader>
        {outfits.length === 0 ? (
          <EmptyOutfitsState
            onSaveLook={() => setSaveOpen(true)}
            hasWardrobe={reachableWardrobes.length > 0}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {outfits.map((outfit) => (
              <OutfitCard
                key={outfit.id}
                outfit={outfit}
                onWear={() => handleApplyOutfit(outfit.id)}
                onEdit={() => setEditingOutfitId(outfit.id)}
                onDelete={() => setDeletingOutfitId(outfit.id)}
                onItemClick={(itemId) => {
                  const item = inventoryById.get(itemId);
                  if (item) setDetailItemId(item.id);
                }}
              />
            ))}
          </div>
        )}
      </section>

      <Separator />

      <section aria-labelledby="worn-heading" className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <SectionHeader id="worn-heading">Currently Worn</SectionHeader>
          <UndressButton equippedCount={equippedItems.length} onUndress={handleUndress} />
        </div>
        <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-[280px_1fr]">
          <PaperDoll
            equipped={equippedDisplay}
            onItemClick={(itemId) => {
              const item = inventoryById.get(itemId);
              if (item) setDetailItemId(item.id);
            }}
          />
          <div className="space-y-2">
            {equippedItems.length === 0 ? (
              <p className="italic text-muted-foreground">Nothing equipped right now.</p>
            ) : (
              equippedItems.map((item) => (
                <ItemCard key={item.id} item={item} onClick={() => setDetailItemId(item.id)} />
              ))
            )}
          </div>
        </div>
      </section>

      <Separator />

      <section aria-labelledby="inventory-heading" className="space-y-4">
        <SectionHeader id="inventory-heading">Inventory</SectionHeader>
        {inventory.length === 0 ? (
          <p className="italic text-muted-foreground">You aren&apos;t carrying anything.</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {inventory.map((item) => (
              <ItemCard key={item.id} item={item} onClick={() => setDetailItemId(item.id)} />
            ))}
          </div>
        )}
      </section>

      <ItemDetailPanel
        item={detailItem}
        open={detailItem !== null}
        onOpenChange={(open) => {
          if (!open) setDetailItemId(null);
        }}
        isEquipped={detailItem ? equippedItemIds.has(detailItem.id) : false}
        characterId={characterId}
        onWear={handleWear}
        onRemove={handleRemove}
        onDrop={handleDrop}
        onGive={handleGiveClick}
        onPutIn={handlePutInClick}
        onDeposit={handleDeposit}
        onSetContainerPolicy={handleSetContainerPolicy}
      />

      <GiveItemDialog
        open={givingItemId !== null}
        onOpenChange={(open) => {
          if (!open) setGivingItemId(null);
        }}
        recipients={roomCharacters}
        onConfirm={handleGiveConfirm}
      />

      <PutInDialog
        open={puttingInItemId !== null}
        onOpenChange={(open) => {
          if (!open) setPuttingInItemId(null);
        }}
        containers={containerCandidates}
        onConfirm={handlePutInConfirm}
      />

      <SaveOutfitDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        characterSheetId={characterSheetId}
        reachableWardrobes={reachableWardrobes}
      />

      <CreateItemDialog open={craftOpen} onOpenChange={setCraftOpen} />

      {editingOutfit && (
        <EditOutfitDialog
          open={editingOutfit !== null}
          onOpenChange={(open) => {
            if (!open) setEditingOutfitId(null);
          }}
          outfit={editingOutfit}
          carriedItems={inventory}
        />
      )}

      {deletingOutfit && (
        <DeleteOutfitDialog
          open={deletingOutfit !== null}
          onOpenChange={(open) => {
            if (!open) setDeletingOutfitId(null);
          }}
          outfit={deletingOutfit}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

interface SectionHeaderProps {
  id: string;
  children: React.ReactNode;
}

function SectionHeader({ id, children }: SectionHeaderProps) {
  return (
    <h2 id={id} className="text-xl font-bold tracking-tight">
      {children}
    </h2>
  );
}

interface EmptyOutfitsStateProps {
  onSaveLook: () => void;
  hasWardrobe: boolean;
}

function EmptyOutfitsState({ onSaveLook, hasWardrobe }: EmptyOutfitsStateProps) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed px-6 py-12 text-center">
      <Shirt className="mb-4 h-12 w-12 text-muted-foreground/50" aria-hidden="true" />
      <p className="mb-1 text-muted-foreground">No saved outfits yet.</p>
      {hasWardrobe ? (
        <>
          <p className="mb-4 text-sm text-muted-foreground">
            Save your current look as your first outfit.
          </p>
          <Button onClick={onSaveLook} variant="outline" size="sm">
            <Plus className="mr-2 h-4 w-4" />
            Save look
          </Button>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          You&apos;ll need a wardrobe item before you can save outfits.
        </p>
      )}
    </div>
  );
}

function NoActiveCharacterState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed px-6 py-16 text-center">
      <Shirt className="mb-4 h-12 w-12 text-muted-foreground/50" aria-hidden="true" />
      <p className="mb-1 text-muted-foreground">Pick a character to manage their wardrobe.</p>
      <p className="text-sm text-muted-foreground">
        Switch characters from the game window to load their outfits and inventory.
      </p>
    </div>
  );
}
